import { v4 as uuidv4 } from "uuid";
import { defaultUnitsV2 } from "../../../../sustell_15/utils/unit-utils";
import { UserProfilePrefs } from "../../../Helpers/UserProfilePrefs";
import {
  createCorePayload,
  mapResourceUseSustell,
} from "./outCommonMapperSustellV2";
import { setInterventionFloatValue } from "./outCommonMapperSustell";
import {
  DairyV2ContinuousGrowingStageData,
  DairyV2DairyCowStageData,
  DairyV2Input,
  DairyV2InputContinuousGrowing,
  DairyV2Intervention,
  DairyV2MilkProduction,
  DairyV2OutputContinuousGrowing,
  DairyV2OutputDairyCow,
  DairyV2Stage
} from "../../../../sustell_15/models/Intervention/DairyV2Intervention";
import {
  CommonStageInputDBModel,
  ContinousGrowingStageInputDBModel,
  ContinuousGrowingOutputDBModel,
  DairyCowOutputDBModel,
  DairyV2CommonStageDataDBModel,
  DairyV2ContinuousGrowingStageDataDBModel,
  DairyV2DairyCowStageDataDBModel,
  EmissionsDBModel
} from "../../../../sustell_15/models/TempBackendModels/TempInterventionModels/TempDairyV2InterventionDB";
import {
  CattleAnimalInputDBModel,
  CattleMilkProductionDBModel,
  CompoundFeedInfoDBModel as TempInterventionCompoundFeedInfoDBModel,
  EmissionMitigationDBModel,
  FeedingProgramCalfDBModel,
  CattleBeddingDBModel as TempInterventionCattleBeddingDBModel,
  CattleManureManagementSystemDBModel as TempInterventionCattleManureManagementSystemDBModel,
} from "../../../../sustell_15/models/TempBackendModels/TempInterventionModels/TempCattleInterventionDB";
import {
  CattleHousingAndManure as InterventionCattleHousingAndManure,
  CattleBeddingSystem as InterventionCattleBeddingSystem,
  CattleManureManagementSystem as InterventionCattleManureManagementSystem,
  CattleEmission,
  CattleFeed,
  InternalSource as InterventionInternalSource,
  ExternalSource as InterventionExternalSource,
  FreshGrass as InterventionFreshGrass
} from "../../../../sustell_15/models/Intervention/CattleIntervention";
import {
  CattleBeddingSystem,
  CattleManureManagementSystem,
  FreshGrass as BaselineFreshGrass
} from "../../../../sustell_15/models/Baseline/CattleBaseline";
import {
  CattleBeddingDBModel,
  CattleManureManagementSystemDBModel,
  CompoundFeedInfoDBModel as BaselineCompoundFeedInfoDBModel,
} from "../../../../sustell_15/models/TempBackendModels/TempCattleDB";
import { floatValueInDefaultUnit } from "../../Baseline/mapper/floatHelperFunctions";
import { CompoundFeedDatabase, StageType } from "../../../../../graphql/types";
import { InputAnimalOrigin } from "../../../../../graphql/generated/blonk/cattle";
import { getDefaultTransportUnit, getUserTransportUnit } from "../../Baseline/mapper/outCommonMapperSustellV2";
import { BaselineFeed, BaselineFeedWithOrigin } from "../../../../sustell_15/models/Baseline/Baseline";
import { InterventionFeed } from "../../../../sustell_15/models/Intervention/Intervention";
import { isFieldFilled } from "../../../../sustell_15/components/FarmFlow/Intervention/CommonDataParts/InterventionGenericInfoDialog";
import { InterventionChangeType } from "../../../../sustell_15/models/TempBackendModels/TempInterventionModels/commons";

const initStageData = (stageType: string) => {
  if (stageType === StageType.DairyCow) {
    return {
      animalProductivity: 'HIGH',
      input: {
        averageWeight: {},
        animals: [],
        animalsAdditions: []
      },
      stockChange: {
        averageWeightCowsStart: {},
        cowsPresentAtEnd: {},
        cowsPresentAtStart: {},
        averageWeightCowsEnd: {},
      },
      feedingProgram: {
        databaseFoundation: CompoundFeedDatabase.Afp_5,
        dietCharacterisation: "FEEDLOT_CORN",
        compoundFeeds: [],
        singleIngredients: [],
        freshGrass: [],
        compoundFeedsAdditions: [],
        singleIngredientsAdditions: [],
        freshGrassAdditions: [],
      },
      milkProduction: {
        amount: {},
        fatContent: {},
        proteinContent: {}
      },
      outputCows: {
        animalsOutgoing: {},
        averageWeight: {},
        calculateLca: false,
        mortalities: {},
        soldExternally: {},
        weightAtMortality: {},
      },
      outputCalves: {
        animalsOutgoing: {},
        calculateLca: false,
        soldExternally: {},
      },
      calvesBirthWeight: {},
      bedding: [],
      beddingAdditions: [],
      mms: [],
      mmsAdditions: [],
      timeSpent: {
        timeInHousingTied: {
          change_type: "" as InterventionChangeType,
          value: null
        },
        timeInFeedlot: {
          change_type: "" as InterventionChangeType,
          value: null
        },
        timeInHousing: {
          change_type: "" as InterventionChangeType,
          value: null
        },
        timeOnLargeGrazingAreas: {
          change_type: "" as InterventionChangeType,
          value: null
        },
        timeOnSmallGrazingAreas: {
          change_type: "" as InterventionChangeType,
          value: null
        },
      },
      emissions: {
        emissionMitigations: [],
      },
    };
  };

  if (stageType === StageType.ContinuousGrowing) {
    return {
      animalProductivity: 'HIGH',
      input: {
        animalType: 'CALF',
        averageAge: {},
        averageWeight: {},
        animals: [],
        animalsAdditions: []
      },
      stockChange: {
        averageWeightCowsStart: {},
        cowsPresentAtEnd: {},
        cowsPresentAtStart: {},
        averageWeightCowsEnd: {},
      },
      outputCows: {
        animalsOutgoing: {},
        averageWeight: {},
        averageAge: {},
        soldExternally: {},
        mortalities: {},
        weightAtMortality: {},
        calculateLca: true,
      },
      feedingProgram: {
        databaseFoundation: CompoundFeedDatabase.Afp_5,
        dietCharacterisation: "FEEDLOT_CORN",
        mothersMilk: {
          amount: {},
          crudeProteinContent: {},
          digestibility: {},
          dryMatterContent: {},
        },
        compoundFeeds: [],
        singleIngredients: [],
        freshGrass: [],
        compoundFeedsAdditions: [],
        singleIngredientsAdditions: [],
        freshGrassAdditions: [],
      },
      timeSpent: {
        timeInFeedlot: {
          change_type: "" as InterventionChangeType,
          value: null
        },
        timeInHousing: {
          change_type: "" as InterventionChangeType,
          value: null
        },
        timeOnLargeGrazingAreas: {
          change_type: "" as InterventionChangeType,
          value: null
        },
        timeOnSmallGrazingAreas: {
          change_type: "" as InterventionChangeType,
          value: null
        },
      },
      bedding: [],
      beddingAdditions: [],
      mms: [],
      mmsAdditions: [],
      emissions: {
        emissionMitigations: [],
      },
    };
  }
};

const mapInputInternalSources = (
  internalSourcesBlockData: DairyV2Input,
  stageGeneralData: CommonStageInputDBModel | ContinousGrowingStageInputDBModel
) => {
  if (
    internalSourcesBlockData.internalSources &&
    Array.isArray(internalSourcesBlockData.internalSources)
  ) {
    internalSourcesBlockData.internalSources?.forEach(
      (item: InterventionInternalSource) => {
        if (item) {
          const inputData = {
            id: item.id || uuidv4(),
            origin: InputAnimalOrigin.ProductionSystem,
            baselineRef: item.baselineRef,
            originStageId: item.originStageId,
            originStageName: item.stageName,
            originFarmName: item.farmName,
            originFarmId: item.farmId,
            stageType: item.stageType,
            animalType: item.animalType === "CALF" ? "CALF" : "COW",
            animalSystemType: item.animalSystemType,
            numberOfAnimals: {},
            shrinkingRate: {},
            transportDistance: {}
          };

          setInterventionFloatValue(
            inputData,
            "transportDistance",
            item,
            "distanceTruck",
            getUserTransportUnit(item.distanceTruck),
            getDefaultTransportUnit(item.distanceTruck)
          );

          setInterventionFloatValue(
            inputData,
            "numberOfAnimals",
            item,
            "numberAnimals",
          );

          setInterventionFloatValue(
            inputData,
            "shrinkingRate",
            item,
            "shrinkingRate"
          );

          stageGeneralData.animals?.push(
            inputData as CattleAnimalInputDBModel
          );
        }
      }
    );
  }
}

const mapInputExternalSources = (
  externalSourcesBlockData: DairyV2Input,
  stageGeneralData: CommonStageInputDBModel | ContinousGrowingStageInputDBModel
) => {
  if (
    externalSourcesBlockData.externalSources &&
    Array.isArray(externalSourcesBlockData.externalSources)
  ) {
    externalSourcesBlockData.externalSources?.forEach(
      (item: InterventionExternalSource) => {
        if (item) {
          const externalSource = {
            id: item.id || uuidv4(),
            transportDistance: {},
            numberOfAnimals: {},
            origin: InputAnimalOrigin.BackgroundData,
            shrinkingRate: {},
          };

          setInterventionFloatValue(
            externalSource,
            "transportDistance",
            item,
            "distanceTruck"
          );

          setInterventionFloatValue(
            externalSource,
            "numberOfAnimals",
            item,
            "numberAnimals",
          );

          setInterventionFloatValue(
            externalSource,
            "shrinkingRate",
            item,
            "shrinkingRate"
          );

          stageGeneralData?.animals?.push(
            externalSource as CattleAnimalInputDBModel
          );
        }
      }
    );
  }
}

const mapInput = (
  inputBlockData: DairyV2Input | DairyV2InputContinuousGrowing,
  stageGeneralData: DairyV2DairyCowStageDataDBModel | DairyV2ContinuousGrowingStageDataDBModel
) => {
  setInterventionFloatValue(
    stageGeneralData.stockChange,
    "cowsPresentAtStart",
    inputBlockData,
    "animalsPresentAtStart"
  );

  setInterventionFloatValue(
    stageGeneralData.stockChange,
    "cowsPresentAtEnd",
    inputBlockData,
    "animalsPresentAtEnd"
  );

  setInterventionFloatValue(
    stageGeneralData.stockChange,
    "averageWeightCowsStart",
    inputBlockData,
    "averageWeightAtStart"
  );

  setInterventionFloatValue(
    stageGeneralData.input,
    "averageWeight",
    inputBlockData,
    "averageWeightNewAnimals"
  );

  if (isFieldFilled(inputBlockData["cattleProductivity"])) {
    stageGeneralData["animalProductivity"] = inputBlockData["cattleProductivity"] as "HIGH" | "LOW";
  }

  mapInputInternalSources(inputBlockData, stageGeneralData.input);
  mapInputExternalSources(inputBlockData, stageGeneralData.input);
}

const mapContinuousGrowingInput = (
  inputBlockData: DairyV2InputContinuousGrowing,
  stageGeneralData: DairyV2ContinuousGrowingStageDataDBModel
) => {
  setInterventionFloatValue(
    stageGeneralData.stockChange,
    "averageWeightCowsEnd",
    inputBlockData,
    "averageWeightAtEnd"
  );

  setInterventionFloatValue(
    stageGeneralData.input,
    "averageAge",
    inputBlockData,
    "averageAge"
  );

  mapInput(inputBlockData, stageGeneralData);
}

const mapCompoundFeeds = (
  feedBlockData: CattleFeed,
  stageGeneralData: FeedingProgramCalfDBModel
) => {
  const userPrefs = UserProfilePrefs.getInstance();
  const userUOM = userPrefs.getUserUnitPrefs();

  if (
    feedBlockData.compoundFeeds &&
    Array.isArray(feedBlockData.compoundFeeds)
  ) {
    feedBlockData.compoundFeeds?.forEach(
      (item: InterventionFeed) => {
        if (item) {
          const compoundFeed = {
            compoundFeedId: item.id || uuidv4(),
            name: item.feedType,
            quantity: {},
          };

          setInterventionFloatValue(
            compoundFeed,
            'quantity',
            item,
            'kgPerAnimal',
            userUOM.unitBarnOutputMass,
            defaultUnitsV2.unitBarnOutputMass
          );

          stageGeneralData.compoundFeeds?.push(
            compoundFeed as TempInterventionCompoundFeedInfoDBModel
          );
        }
      });
  }

  if (
    feedBlockData.compoundFeedsAdditions &&
    Array.isArray(feedBlockData.compoundFeedsAdditions)
  ) {
    feedBlockData.compoundFeedsAdditions?.forEach(
      (item: BaselineFeed) => {
        if (item) {
          const compoundFeedAddition = {
            compoundFeedId: item.id || uuidv4(),
            name: item.feedType,
            quantity: floatValueInDefaultUnit(
              item.kgPerAnimal,
              userUOM.unitBarnOutputMass,
              defaultUnitsV2.unitBarnOutputMass
            ),
          };

          stageGeneralData.compoundFeedsAdditions?.push(
            compoundFeedAddition as BaselineCompoundFeedInfoDBModel
          );
        }
      });
  }
};

const mapSingleIngredients = (
  feedBlockData: CattleFeed,
  stageGeneralData: FeedingProgramCalfDBModel
) => {
  const userPrefs = UserProfilePrefs.getInstance();
  const userUOM = userPrefs.getUserUnitPrefs();

  if (
    feedBlockData.singleIngredients &&
    Array.isArray(feedBlockData.singleIngredients)
  ) {
    feedBlockData.singleIngredients?.forEach(
      (item: InterventionFeed) => {
        if (item && Number(item.kgPerAnimal) !== 0) {
          const singleIngredient = {
            ingredient: {
              id: item.id || uuidv4(),
              name: item.feedType,
              origin: item.origin
            },
            quantity: {}
          };

          if (item.kgPerAnimal || item.kgPerAnimal === 0) {
            singleIngredient.quantity = {};

            setInterventionFloatValue(
              singleIngredient,
              'quantity',
              item,
              'kgPerAnimal',
              userUOM.unitBarnOutputMass,
              defaultUnitsV2.unitBarnOutputMass
            );
          }

          stageGeneralData.singleIngredients?.push(
            singleIngredient
          );
        }
      });
  }

  if (
    feedBlockData.singleIngredientsAdditions &&
    Array.isArray(feedBlockData.singleIngredientsAdditions)
  ) {
    feedBlockData.singleIngredientsAdditions?.forEach(
      (item: BaselineFeedWithOrigin) => {
        if (item && Number(item.kgPerAnimal) !== 0) {
          const singleIngredientAddition = {
            ingredient: {
              id: item.id || uuidv4(),
              name: item.feedType,
              origin: item.origin,
            },
            quantity: floatValueInDefaultUnit(
              item.kgPerAnimal,
              userUOM.unitBarnOutputMass,
              defaultUnitsV2.unitBarnOutputMass
            ),
          };

          stageGeneralData.singleIngredientsAdditions?.push(
            singleIngredientAddition
          );
        }
      });
  }
};

const mapFreshGrass = (
  feedBlockData: CattleFeed,
  stageGeneralData: FeedingProgramCalfDBModel
) => {
  if (
    feedBlockData.freshGrass &&
    Array.isArray(feedBlockData.freshGrass)
  ) {
    feedBlockData.freshGrass?.forEach(
      (item: InterventionFreshGrass) => {
        const freshGrass = {
          id: item.id || uuidv4(),
          type: item.type as string,
          amount: {},
          crudeProteinContent: {},
          dryMatterContent: {},
          digestibility: {},
          neutralDetergentFiber: {},
          grossEnergyContent: {},
          ashContent: {}
        };

        setInterventionFloatValue(
          freshGrass,
          'amount',
          item,
          'amount',
        );

        setInterventionFloatValue(
          freshGrass,
          'crudeProteinContent',
          item,
          'crudeProteinContent',
        );

        setInterventionFloatValue(
          freshGrass,
          'dryMatterContent',
          item,
          'dryMatterContent',
        );

        setInterventionFloatValue(
          freshGrass,
          'digestibility',
          item,
          'digestibility',
        );

        setInterventionFloatValue(
          freshGrass,
          'neutralDetergentFiber',
          item,
          'neutralDetergentFiber',
        );

        setInterventionFloatValue(
          freshGrass,
          'grossEnergyContent',
          item,
          'grossEnergyContent',
        );

        setInterventionFloatValue(
          freshGrass,
          'ashContent',
          item,
          'ashContent',
        );

        stageGeneralData.freshGrass?.push(
          freshGrass
        );
      });
  }

  if (
    feedBlockData.freshGrassAdditions &&
    Array.isArray(feedBlockData.freshGrassAdditions)
  ) {
    feedBlockData.freshGrassAdditions?.forEach(
      (item: BaselineFreshGrass) => {
        const grassAddition = {
          id: item.id || uuidv4(),
          type: item.type,
          amount: floatValueInDefaultUnit(
            item.amount,
          ),
          crudeProteinContent: floatValueInDefaultUnit(
            item.crudeProteinContent,
          ),
          dryMatterContent: floatValueInDefaultUnit(
            item.dryMatterContent,
          ),
          digestibility: floatValueInDefaultUnit(
            item.digestibility,
          ),
          neutralDetergentFiber: floatValueInDefaultUnit(
            item.neutralDetergentFiber,
          ),
          grossEnergyContent: floatValueInDefaultUnit(
            item.grossEnergyContent,
          ),
          ashContent: floatValueInDefaultUnit(
            item.ashContent,
          ),
        };

        stageGeneralData.freshGrassAdditions?.push(
          grassAddition
        );
      });
  }
};


const mapFeed = (
  feedBlockData: CattleFeed,
  stageGeneralData: FeedingProgramCalfDBModel
) => {
  mapCompoundFeeds(feedBlockData, stageGeneralData);
  mapSingleIngredients(feedBlockData, stageGeneralData);
  mapFreshGrass(feedBlockData, stageGeneralData);
}

const mapContinuousGrowingFeed = (
  feedBlockData: CattleFeed,
  stageGeneralData: FeedingProgramCalfDBModel
) => {
  if (
    feedBlockData.mothersMilk?.amount ||
    Number(feedBlockData.mothersMilk?.amount) === 0
  )
    setInterventionFloatValue(
      stageGeneralData.mothersMilk,
      "amount",
      feedBlockData.mothersMilk,
      "amount"
    );

  if (
    feedBlockData.mothersMilk?.crudeProteinContent ||
    Number(feedBlockData.mothersMilk?.crudeProteinContent) === 0
  )
    setInterventionFloatValue(
      stageGeneralData.mothersMilk,
      "crudeProteinContent",
      feedBlockData.mothersMilk,
      "crudeProteinContent"
    );

  if (
    feedBlockData.mothersMilk?.digestibility ||
    Number(feedBlockData.mothersMilk?.digestibility) === 0
  )
    setInterventionFloatValue(
      stageGeneralData.mothersMilk,
      "digestibility",
      feedBlockData.mothersMilk,
      "digestibility"
    );

  if (
    feedBlockData.mothersMilk?.dryMatterContent ||
    Number(feedBlockData.mothersMilk?.dryMatterContent) === 0
  )
    setInterventionFloatValue(
      stageGeneralData.mothersMilk,
      "dryMatterContent",
      feedBlockData.mothersMilk,
      "dryMatterContent"
    );

  mapFeed(feedBlockData, stageGeneralData);
}

const mapBeddingType = (
  beddingBlockData: InterventionCattleHousingAndManure,
  stageGeneralData: DairyV2CommonStageDataDBModel
) => {
  const userPrefs = UserProfilePrefs.getInstance();
  const userUOM = userPrefs.getUserUnitPrefs();
  const outputMassPrecision = userPrefs.getUnitOutputMassPrecision();
  const { bedding } = stageGeneralData;

  if (
    beddingBlockData.beddingSystems &&
    Array.isArray(beddingBlockData.beddingSystems)
  ) {
    beddingBlockData.beddingSystems.forEach(
      (item: InterventionCattleBeddingSystem) => {
        if (item) {
          const beddingType = {
            id: item.id || uuidv4(),
            type: item.beddingType,
            amount: {}
          }

          setInterventionFloatValue(
            beddingType,
            "amount",
            item,
            "beddingAmount",
            userUOM.unitOutputMass,
            defaultUnitsV2.unitOutputMass,
            outputMassPrecision
          );

          bedding?.push(
            beddingType as TempInterventionCattleBeddingDBModel
          );
        }
      }
    )
  }

  if (
    beddingBlockData.beddingSystemsAdditions &&
    Array.isArray(beddingBlockData.beddingSystemsAdditions)
  ) {
    beddingBlockData.beddingSystemsAdditions.forEach(
      (item: CattleBeddingSystem) => {
        if (item) {
          const beddingAddition = {
            id: item.id || uuidv4(),
            type: item.beddingType,
            amount: floatValueInDefaultUnit(
              item.beddingAmount,
              userUOM.unitBarnOutputMass,
              defaultUnitsV2.unitBarnOutputMass
            ),
          }

          stageGeneralData?.beddingAdditions?.push(
            beddingAddition as CattleBeddingDBModel
          );
        }
      }
    )
  }
}

const mapMMSType = (
  mmsBlockData: InterventionCattleHousingAndManure,
  stageGeneralData: DairyV2CommonStageDataDBModel
) => {
  const { manureSystems } = mmsBlockData;
  const { mms } = stageGeneralData;

  if (
    manureSystems &&
    Array.isArray(manureSystems)
  ) {
    manureSystems.forEach(
      (item: InterventionCattleManureManagementSystem) => {
        if (item) {
          const mmsType = {
            id: item.id || uuidv4(),
            share: {},
            mmsType: item.mmsType,
            storagePeriod: {},
            manurePhase: {},
          }

          setInterventionFloatValue(
            mmsType,
            "storagePeriod",
            item,
            "storagePeriod",
          );

          setInterventionFloatValue(
            mmsType,
            "share",
            item,
            "share",
          );

          setInterventionFloatValue(
            mmsType,
            "manurePhase",
            item,
            "manurePhase",
          );

          mms?.push(
            mmsType as TempInterventionCattleManureManagementSystemDBModel
          );
        }
      }
    )
  }

  if (
    mmsBlockData.manureSystemsAdditions &&
    Array.isArray(mmsBlockData.manureSystemsAdditions)
  ) {
    mmsBlockData.manureSystemsAdditions.forEach(
      (item: CattleManureManagementSystem) => {
        if (item) {
          const mmsAddition = {
            id: item.id || uuidv4(),
            mmsType: item.mmsType,
            storagePeriod: floatValueInDefaultUnit(
              item.storagePeriod,
            ),
            share: floatValueInDefaultUnit(
              item.share,
            ),
            manurePhase: floatValueInDefaultUnit(
              item.manurePhase,
            ),
          }

          stageGeneralData.mmsAdditions?.push(
            mmsAddition as CattleManureManagementSystemDBModel
          );
        }
      }
    )
  }
}

const mapHousingAndManureDairyCow = (
  housingBlockData: InterventionCattleHousingAndManure,
  stageGeneralData: DairyV2CommonStageDataDBModel
) => {
  if (
    housingBlockData.timeInHousingTied ||
    Number(housingBlockData.timeInHousingTied) === 0
  )
    setInterventionFloatValue(
      stageGeneralData.timeSpent,
      "timeInHousingTied",
      housingBlockData,
      "timeInHousingTied"
    );

  mapHousingAndManure(housingBlockData, stageGeneralData);
}

const mapHousingAndManure = (
  housingBlockData: InterventionCattleHousingAndManure,
  stageGeneralData: DairyV2CommonStageDataDBModel
) => {
  if (
    housingBlockData.timeInHousing ||
    Number(housingBlockData.timeInHousing) === 0
  )
    setInterventionFloatValue(
      stageGeneralData.timeSpent,
      "timeInHousing",
      housingBlockData,
      "timeInHousing"
    );

  if (
    housingBlockData.timeInFeedlot ||
    Number(housingBlockData.timeInFeedlot) === 0
  )
    setInterventionFloatValue(
      stageGeneralData.timeSpent,
      "timeInFeedlot",
      housingBlockData,
      "timeInFeedlot"
    );

  if (
    housingBlockData.timeGrazingLargeAreas ||
    Number(housingBlockData.timeGrazingLargeAreas) === 0
  )
    setInterventionFloatValue(
      stageGeneralData.timeSpent,
      "timeOnLargeGrazingAreas",
      housingBlockData,
      "timeGrazingLargeAreas"
    );

  if (
    housingBlockData.timeGrazingPastures ||
    Number(housingBlockData.timeGrazingPastures) === 0
  )
    setInterventionFloatValue(
      stageGeneralData.timeSpent,
      "timeOnSmallGrazingAreas",
      housingBlockData,
      "timeGrazingPastures"
    );

  mapBeddingType(housingBlockData, stageGeneralData);
  mapMMSType(housingBlockData, stageGeneralData);
}

const mapDairyCowOutputMilk = (
  milkBlockData: DairyV2MilkProduction,
  stageGeneralData: CattleMilkProductionDBModel
) => {
  const userPrefs = UserProfilePrefs.getInstance();
  const userUOM = userPrefs.getUserUnitPrefs();
  
  if (
    milkBlockData.yearlyMilkProduction ||
    Number(milkBlockData.yearlyMilkProduction) === 0
  )
    setInterventionFloatValue(
      stageGeneralData,
      "amount",
      milkBlockData,
      "yearlyMilkProduction",
      userUOM.unitOutputMass,
      defaultUnitsV2.unitOutputMass
    );

  if (
    milkBlockData.averageMilkFat ||
    Number(milkBlockData.averageMilkFat) === 0
  )
    setInterventionFloatValue(
      stageGeneralData,
      "fatContent",
      milkBlockData,
      "averageMilkFat",
    );

  if (
    milkBlockData.averageMilkProtein ||
    Number(milkBlockData.averageMilkProtein) === 0
  )
    setInterventionFloatValue(
      stageGeneralData,
      "proteinContent",
      milkBlockData,
      "averageMilkProtein",
    );
}

const mapOutputCows = (
  outputBlockData: DairyV2OutputDairyCow | DairyV2OutputContinuousGrowing,
  stageGeneralData: DairyCowOutputDBModel | ContinuousGrowingOutputDBModel
) => {
  const userPrefs = UserProfilePrefs.getInstance();
  const userUOM = userPrefs.getUserUnitPrefs();
  const outputMassPrecision = userPrefs.getUnitOutputMassPrecision();

  if (
    outputBlockData.cowsToAnotherStage ||
    Number(outputBlockData.cowsToAnotherStage) === 0
  )
    setInterventionFloatValue(
      stageGeneralData,
      "animalsOutgoing",
      outputBlockData,
      "cowsToAnotherStage",
    );

  if (
    outputBlockData.averageWeightOfCowsLeavingTheStage ||
    Number(outputBlockData.averageWeightOfCowsLeavingTheStage) === 0
  )
    setInterventionFloatValue(
      stageGeneralData,
      "averageWeight",
      outputBlockData,
      "averageWeightOfCowsLeavingTheStage",
      userUOM.unitOutputMass,
      defaultUnitsV2.unitOutputMass,
      outputMassPrecision
    );

  if (
    outputBlockData.cowsSold ||
    Number(outputBlockData.cowsSold) === 0
  )
    setInterventionFloatValue(
      stageGeneralData,
      "soldExternally",
      outputBlockData,
      "cowsSold",
    );

  if (
    outputBlockData.numberOfMortalitiesCows ||
    Number(outputBlockData.numberOfMortalitiesCows) === 0
  )
    setInterventionFloatValue(
      stageGeneralData,
      "mortalities",
      outputBlockData,
      "numberOfMortalitiesCows",
    );

  if (
    outputBlockData.averageWeightAtMortalityCows ||
    Number(outputBlockData.averageWeightAtMortalityCows) === 0
  )
    setInterventionFloatValue(
      stageGeneralData,
      "weightAtMortality",
      outputBlockData,
      "averageWeightAtMortalityCows",
      userUOM.unitOutputMass,
      defaultUnitsV2.unitOutputMass,
      outputMassPrecision
    );
}

const mapDairyCowOutputCalves = (
  calvesBlockData: DairyV2OutputDairyCow,
  stageGeneralData: DairyV2DairyCowStageDataDBModel
) => {
  const userPrefs = UserProfilePrefs.getInstance();
  const userUOM = userPrefs.getUserUnitPrefs();

  if (
    calvesBlockData.weanedCalvesToAnotherStage ||
    Number(calvesBlockData.weanedCalvesToAnotherStage) === 0
  )
    setInterventionFloatValue(
      stageGeneralData.outputCalves,
      "animalsOutgoing",
      calvesBlockData,
      "weanedCalvesToAnotherStage",
    );

  if (
    calvesBlockData.weanedCalvesSold ||
    Number(calvesBlockData.weanedCalvesSold) === 0
  )
    setInterventionFloatValue(
      stageGeneralData.outputCalves,
      "soldExternally",
      calvesBlockData,
      "weanedCalvesSold",
    );

  if (
    calvesBlockData.averageWeightAtBirth ||
    Number(calvesBlockData.averageWeightAtBirth) === 0
  )
    setInterventionFloatValue(
      stageGeneralData,
      "calvesBirthWeight",
      calvesBlockData,
      "averageWeightAtBirth",
      userUOM.unitOutputMass,
      defaultUnitsV2.unitOutputMass
    );
}

const mapContinuousGrowingOutput = (
  outputBlockData: DairyV2OutputContinuousGrowing,
  stageGeneralData: ContinuousGrowingOutputDBModel
) => {
  if (
    outputBlockData.averageAge ||
    Number(outputBlockData.averageAge) === 0
  )
    setInterventionFloatValue(
      stageGeneralData,
      "averageAge",
      outputBlockData,
      "averageAge",
    );

  mapOutputCows(outputBlockData, stageGeneralData);
}

const mapEmissionMitigations = (
  emissionsBlockData: CattleEmission,
  stageGeneralData: EmissionsDBModel
) => {
  if (!emissionsBlockData) return [];
  const entries = Object.entries(emissionsBlockData);
  if (!entries.length) return [];
  const mappedEmissions: EmissionMitigationDBModel[] = [];

  entries.forEach((item) => {
    if (
      !item[0].includes("_changeMetric") &&
      !item[0].includes("_newValue") &&
      !item[0].includes("original")
    ) {
      const changeType = entries.find((emission) => {
        emission[0] === `${item[0]}_changeMetric`
      });
      mappedEmissions.push(
        {
          emissionType: item[0],
          change: {
            value: item[1],
            change_type: changeType ? changeType[1] : 'relative',
          }
        }
      )
    }
  })

  stageGeneralData.emissionMitigations = mappedEmissions;
};

const mapStageInterventionDataOutSustell = (stageBlockData: DairyV2Stage) => {
  const { stageData } = stageBlockData;

  const oneStageData = {
    id: stageBlockData?.id || uuidv4(),
    name: stageBlockData?.name,
    type: stageBlockData?.type,
    startDate: stageBlockData?.startDate,
    stageData: { ...initStageData(stageBlockData.type as string) },
  };

  setInterventionFloatValue(
    stageBlockData,
    "startDate",
    oneStageData,
    "startDate"
  );

  if (stageData) {
    if (stageBlockData.type == StageType.DairyCow) {
      const oneStageDataDairyCow = oneStageData.stageData as DairyV2DairyCowStageDataDBModel;
      const { input, feed, housing, output, emissions } = stageData as DairyV2DairyCowStageData;

      if (input && oneStageDataDairyCow) {
        mapInput(input, oneStageDataDairyCow);
      }

      if (feed && oneStageDataDairyCow.feedingProgram)
        mapFeed(feed, oneStageDataDairyCow.feedingProgram);

      if (housing && oneStageDataDairyCow)
        mapHousingAndManureDairyCow(housing, oneStageDataDairyCow);

      if (output && oneStageDataDairyCow) {
        mapOutputCows(output, oneStageDataDairyCow.outputCows);
        mapDairyCowOutputMilk(output, oneStageDataDairyCow.milkProduction);
        mapDairyCowOutputCalves(output, oneStageDataDairyCow);
      }

      if (emissions?.emissionMitigations && oneStageDataDairyCow.emissions)
        mapEmissionMitigations(emissions?.emissionMitigations, oneStageDataDairyCow.emissions);
    }

    if (stageBlockData.type == StageType.ContinuousGrowing) {
      const oneStageDataContinuousGrowing = oneStageData.stageData as DairyV2ContinuousGrowingStageDataDBModel;
      const { input, feed, housing, output, emissions } = stageData as DairyV2ContinuousGrowingStageData;

      if (input && oneStageDataContinuousGrowing) {
        oneStageDataContinuousGrowing.input.animals = [];
        mapContinuousGrowingInput(input, oneStageDataContinuousGrowing);
      }

      if (feed && oneStageDataContinuousGrowing.feedingProgram)
        mapContinuousGrowingFeed(feed, oneStageDataContinuousGrowing.feedingProgram);

      if (housing && oneStageDataContinuousGrowing)
        mapHousingAndManure(housing, oneStageDataContinuousGrowing);

      if (output && oneStageDataContinuousGrowing.outputCows)
        mapContinuousGrowingOutput(output, oneStageDataContinuousGrowing.outputCows);

      if (emissions?.emissionMitigations && oneStageDataContinuousGrowing.emissions)
        mapEmissionMitigations(emissions?.emissionMitigations, oneStageDataContinuousGrowing.emissions);
    }
  }

  return oneStageData;
};

const mapDairyV2InterventionDataOutSustell = (formData: DairyV2Intervention) => {
  let payloadObj = createCorePayload(formData);
  payloadObj = mapResourceUseSustell(formData, payloadObj);

  if (formData.stages && Array.isArray(formData.stages)) {
    return formData.stages.reduce(
      (payload, stage) => {
        if (!stage?.stageData) return payloadObj;

        const mappedStage = mapStageInterventionDataOutSustell(stage);

        return {
          ...payload,
          stages: [
            ...payload.stages,
            {
              ...mappedStage,
              stageData: JSON.stringify(mappedStage.stageData),
            },
          ],
        };
      },
      { ...payloadObj }
    );
  }

  return payloadObj;
};

export default mapDairyV2InterventionDataOutSustell;
